/**
 * CompanyKPIBadges — shows EBITDA% and ROIC badges for a company card.
 * Used in CompanyResearch list to surface key KPIs without navigating to the detail page.
 * Silently renders nothing if financial data has not been ingested yet.
 *
 * This component is now purely presentational. The parent MUST pass the
 * `summary` prop (fetched via the bulk `getAllSummary` API). No per-card
 * individual summary request is ever made — that previously caused N extra
 * /summary XHRs alongside the bulk call.
 */
import type { CompanySummaryResponse } from '../../../types/financial';

interface Props {
  companyName: string;
  /** Pre-fetched summary from the bulk getAllSummary call. */
  summary?: CompanySummaryResponse;
  /**
   * True while the bulk summary request is still in flight. The EBITDA / ROIC /
   * EV-EBITDA figures arrive from that call AFTER the company list itself, so
   * without this the badge row is simply blank while they load with nothing to
   * signal that data is on the way. Renders shimmer pills in that window.
   */
  loading?: boolean;
}

export default function CompanyKPIBadges({ summary, loading }: Props) {
  if (!summary) {
    if (loading) {
      return (
        <div className="flex items-center gap-2 mt-2" aria-hidden="true">
          <div className="h-5 w-20 rounded-full bg-neutral-100 animate-pulse" />
          <div className="h-5 w-28 rounded-full bg-neutral-100 animate-pulse" />
          <div className="h-5 w-24 rounded-full bg-neutral-100 animate-pulse" />
        </div>
      );
    }
    return null;
  }

  // The latest year needs BOTH statements for these KPIs to mean anything: with
  // the Balance Sheet absent, invested capital is 0 so ROIC reads a flat 0.0%
  // and EV misses net debt. Hide the badges rather than show numbers the rest of
  // the app is simultaneously reporting as unavailable. (Backend sends these on
  // the summary; `undefined` from an older response leaves the prior behaviour.)
  if (summary.has_balance_sheet === false || summary.has_profit_loss === false) return null;

  const ebitdaPct =
    summary.ebitda_margin_pct ?? (summary.ebitda_margin != null ? summary.ebitda_margin * 100 : null);
  const roicPct =
    summary.roic_pct ?? (summary.roic != null ? summary.roic * 100 : null);
  const waccPct =
    summary.wacc_pct ?? (summary.wacc != null ? summary.wacc * 100 : null);

  if (ebitdaPct == null && roicPct == null) return null;

  const roicWaccSpread = roicPct != null && waccPct != null ? roicPct - waccPct : null;

  return (
    <div className="flex items-center gap-2 mt-2 flex-wrap">
      {ebitdaPct != null && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200">
          EBITDA {ebitdaPct.toFixed(1)}%
        </span>
      )}
      {roicPct != null && (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${
            roicWaccSpread != null && roicWaccSpread >= 0
              ? 'bg-violet-50 text-violet-700 border-violet-200'
              : 'bg-rose-50 text-rose-700 border-rose-200'
          }`}
        >
          ROIC {roicPct.toFixed(1)}%
          {roicWaccSpread != null && (
            <span className="ml-1 opacity-70">
              ({roicWaccSpread >= 0 ? '+' : ''}
              {roicWaccSpread.toFixed(1)}pp)
            </span>
          )}
        </span>
      )}
      {summary.ev_ebitda != null && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-200">
          EV/EBITDA {summary.ev_ebitda.toFixed(1)}x
        </span>
      )}
    </div>
  );
}
